import { vlogCmsAllowed } from "@/lib/artistVlogAccess";
import { ArtistVlogOperations, ArtistVlogQueue, ArtistVlogCoverCleanup } from "@/components/video/ArtistVlogOperations";
import { ArtistVlogPreview } from "@/components/video/ArtistVlogPreview";
import { useEffect, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/services/firebase/init";
import {
  ArtistDraft,
  DraftSummary,
  WorkspacePage,
  WorkspaceRole,
  WorkspaceError,
  workspaceRequest,
} from "@/services/firebase/artistWorkspace";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AdminErrorAlert, AdminStatusBadge } from "@/components/admin-ui";
import { useArtistConfirm } from "./ArtistConfirm";

const labels = {
  draft: "草稿",
  pending_review: "待審核",
  rejected: "退回修改",
  published: "已發布",
  unpublished: "已下架",
  deleted: "已刪除",
};
const typeLabels = { post: "圖文", vlog: "Vlog", story: "限動" };
// B-19: stories live 24 h from publication; rows show the remaining time (or 已到期).
const storyCountdown = (expiresAt?: number | null, now = Date.now()) => {
  if (!expiresAt) return "";
  const left = expiresAt - now;
  if (left <= 0) return "已到期";
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  return h > 0 ? `剩 ${h} 小時 ${m} 分` : `剩 ${m} 分`;
};
// Publish/submit readiness per type: Vlog needs a ready video + cover; a story exactly one image (uploaded in the App);
// Posts require photos; private text drafts remain saveable until an App upload is added.
const notReady = (d: ArtistDraft) =>
  d.type === "vlog"
    ? d.video?.status !== "ready" || !d.coverAssetId
    : d.type === "story"
      ? d.media?.length !== 1
      : !d.media?.length;
const fresh = (artistId: string, type: "post" | "vlog"): ArtistDraft => ({
  artistId,
  contentId: crypto.randomUUID(),
  type,
  audience: "public",
  title: "",
  body: "",
  status: "draft",
  version: 0,
});

/**
 * 藝人內容工作區（dialog）. Member / activation management moved to ArtistMembersPanel (O3).
 * `fansEnabled` (RC artist_fans_enabled): when off, new drafts cannot choose 鐵粉限定; existing fans-only content
 * keeps its audience (never auto-converted to public).
 */
export default function ArtistWorkspace({ artistId, fansEnabled = false }: { artistId: string; fansEnabled?: boolean }) {
  const { confirm, dialog } = useArtistConfirm();
  const [pendingPublication, setPendingPublication] = useState<
    "contentPublish" | "contentUnpublish" | "contentDelete" | null
  >(null);
  const [socialEnabled, setSocialEnabled] = useState(false);
  const [vlogCmsEnabled, setVlogCmsEnabled] = useState(false);
  const [canPublish, setCanPublish] = useState(false);
  const mutations = useRef(new Map<string, string>());
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<DraftSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [role, setRole] = useState<WorkspaceRole | null>(null);
  const [draft, setDraft] = useState<ArtistDraft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const generation = useRef(0);
  const locked = useRef(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      generation.current++;
      locked.current = false;
      setOpen(false);
      setDraft(null);
      setPendingPublication(null);
      setItems([]);
      setRole(null);
      setSocialEnabled(false);
      setVlogCmsEnabled(false);
      setCanPublish(false);
      mutations.current.clear();
      setDirty(false);
      setError("");
      setNotice("");
      setBusy(false);
    });
    const warn = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    const invalidate = () => {generation.current++;};
    return () => {
      invalidate();
      unsubscribe();
      window.removeEventListener("beforeunload", warn);
    };
  }, []);
  // O5: unsaved-draft guard via ConfirmDialog; runs `next` right away when nothing is dirty.
  const discard = (next: () => void) => {
    if (!dirty) return next();
    confirm({
      title: "放棄未儲存的修改？",
      message: "草稿尚未儲存，確定放棄這次修改？",
      confirmText: "放棄修改",
      danger: true,
      onConfirm: next,
    });
  };
  async function run(work: (epoch: number) => Promise<void>) {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    const epoch = generation.current;
    try {
      await work(epoch);
    } catch (e) {
      if (epoch === generation.current) {
        if (
          e instanceof WorkspaceError &&
          ["FORBIDDEN", "UNAUTHENTICATED"].includes(e.code)
        ) {
          setDraft(null);
          setItems([]);
          setRole(null);
          setDirty(false);
        }
        setError(e instanceof Error ? e.message : "操作未完成");
      }
    } finally {
      if (epoch === generation.current) {
        locked.current = false;
        setBusy(false);
      }
    }
  }
  async function load(epoch: number, more = false) {
    if (epoch !== generation.current) return;
    setVlogCmsEnabled(false);
    setSocialEnabled(false);
    setPendingPublication(null);
    const [page, access] = await Promise.all([
      workspaceRequest<WorkspacePage>("workspaceList", artistId, { ...(more && cursor ? { cursor } : {}), limit: 20 }),
      workspaceRequest<{ enabled: boolean; vlogCms?: boolean }>("contentAccess", artistId),
    ]);
    if (epoch !== generation.current) return;
    setSocialEnabled(access.enabled === true);
    setVlogCmsEnabled(vlogCmsAllowed(access));
    setRole(page.role);
    setCanPublish(page.canPublish === true);
    setCursor(page.nextCursor);
    setItems((old) =>
      more
        ? [
            ...new Map(
              [...old, ...page.items].map((i) => [i.contentId, i]),
            ).values(),
          ]
        : page.items,
    );
  }
  function launch() {
    setOpen(true);
    setSocialEnabled(false);
    setVlogCmsEnabled(false);
    void run((epoch) => load(epoch));
  }
  function select(contentId: string) {
    setPendingPublication(null);
    discard(() => void run(async (epoch) => {
      const result = await workspaceRequest<
        ArtistDraft & { role: WorkspaceRole }
      >("workspaceGet", artistId, { contentId });
      if (epoch !== generation.current) return;
      setDraft(result);
      setRole(result.role);
      setDirty(false);
      setReviewNote("");
    }));
  }
  function edit(patch: Partial<ArtistDraft>) {
    setPendingPublication(null);
    setDraft((old) => (old ? { ...old, ...patch } : old));
    setDirty(true);
  }
  function save() {
    if (!draft) return;
    void run(async (epoch) => {
      const saved = await workspaceRequest<ArtistDraft>(
        "workspaceSave",
        artistId,
        {
          contentId: draft.contentId,
          expectedVersion: draft.version,
          type: draft.type,
          audience: draft.audience,
          title: draft.title,
          body: draft.body,
        },
      );
      if (epoch !== generation.current) return;
      setDraft(saved);
      setDirty(false);
      setNotice("草稿已儲存，尚未公開。");
      await load(epoch);
    });
  }
  function transition(action: "workspaceSubmit" | "workspaceReturn") {
    if (!draft) return;
    void run(async (epoch) => {
      await workspaceRequest(action, artistId, {
        contentId: draft.contentId,
        expectedVersion: draft.version,
        reviewNote,
      });
      const saved = await workspaceRequest<ArtistDraft>(
        "workspaceGet",
        artistId,
        { contentId: draft.contentId },
      );
      if (epoch !== generation.current) return;
      setDraft(saved);
      setDirty(false);
      setReviewNote("");
      setNotice(
        action === "workspaceSubmit"
          ? "已送審；內容仍未公開。"
          : "已退回修改。",
      );
      await load(epoch);
    });
  }
  function publish(action: "contentPublish" | "contentUnpublish") {
    if (!draft || dirty) return;
    const verb = action === "contentPublish" ? "發布" : "下架";
    setPendingPublication(null);
    const key = `${draft.contentId}:${draft.version}:${action}`;
    const mutationId = mutations.current.get(key) || crypto.randomUUID();
    mutations.current.set(key, mutationId);
    void run(async (epoch) => {
      await workspaceRequest(action, artistId, {
        contentId: draft.contentId,
        expectedVersion: draft.version,
        mutationId,
      });
      const saved = await workspaceRequest<ArtistDraft>(
        "workspaceGet",
        artistId,
        { contentId: draft.contentId },
      );
      if (epoch !== generation.current) return;
      setDraft(saved);
      setDirty(false);
      setNotice(`已${verb}。`);
      await load(epoch);
    });
  }
  // B-17: fans → public only (public → fans is refused by the server: CDN caches). Copies media to the
  // public path, re-projects the live revision and mirrors the draft's audience (version +1).
  function makePublic() {
    if (!draft || dirty) return;
    setPendingPublication(null);
    void run(async (epoch) => {
      await workspaceRequest("contentSetAudience", artistId, {
        contentId: draft.contentId,
        audience: "public",
      });
      const saved = await workspaceRequest<ArtistDraft>(
        "workspaceGet",
        artistId,
        { contentId: draft.contentId },
      );
      if (epoch !== generation.current) return;
      setDraft(saved);
      setDirty(false);
      setNotice("已改為所有人可看；鐵粉限定無法再改回。");
      await load(epoch);
    });
  }
  // B-05: soft delete (artist role or staff). The draft stays listed as 已刪除, read-only.
  function remove() {
    if (!draft || dirty) return;
    setPendingPublication(null);
    void run(async (epoch) => {
      await workspaceRequest("contentDelete", artistId, {
        contentId: draft.contentId,
      });
      const saved = await workspaceRequest<ArtistDraft>(
        "workspaceGet",
        artistId,
        { contentId: draft.contentId },
      );
      if (epoch !== generation.current) return;
      setDraft(saved);
      setDirty(false);
      setNotice("已刪除；粉絲端與所有列表不再顯示此內容。");
      await load(epoch);
    });
  }
  const editable =
    role !== null &&
    role !== "reviewer" &&
    draft !== null &&
    draft.status !== "pending_review" &&
    draft.status !== "deleted" &&
    (draft.type !== "vlog" || vlogCmsEnabled);
  return (
    <>
      <Button variant="outline" className="mb-6" onClick={launch}>
        內容草稿工作區
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          if (!value && !busy)
            discard(() => {
              setOpen(false);
              setDraft(null);
              setDirty(false);
            });
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto [&_button]:max-w-full [&_button]:whitespace-normal [&_button]:h-auto">
          <DialogHeader>
            <DialogTitle>藝人內容工作區</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {vlogCmsEnabled ? "圖文與 Vlog 可存稿、送審，由獲授權帳號發布。影片與封面先由 App 上傳，Vlog 可在此預覽及審核。" : "圖文與限動可存稿、送審，由獲授權帳號發布。Vlog 目前未開放。"}
          </DialogDescription>
          <p className="text-sm">離開此頁前請先儲存。</p>
          {!fansEnabled && (
            <p className="text-sm text-muted-foreground">
              鐵粉功能目前未開放：新草稿只能設為「所有人」；既有的鐵粉限定內容維持原受眾，不會自動公開。
            </p>
          )}
          {error && (
            <div ref={errorRef} tabIndex={-1}>
              <AdminErrorAlert message={error} />
            </div>
          )}
          {notice && <p role="status">{notice}</p>}
          {busy && <p role="status">處理中…</p>}
          {role === "staff" && <ArtistVlogQueue artistId={artistId} />}
          <div className="grid gap-6 md:grid-cols-[240px_1fr]">
            <aside className="space-y-3">
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => void run((epoch) => load(epoch))}
              >
                重新整理清單
              </Button>
              {role && role !== "reviewer" && (
                <div className="flex flex-wrap gap-2">
                  {(["post", ...(vlogCmsEnabled ? ["vlog"] as const : [])] as const).map((type) => (
                    <Button
                      key={type}
                      disabled={busy}
                      onClick={() =>
                        discard(() => {
                          setDraft(fresh(artistId, type));
                          setDirty(false);
                          setError("");
                          setNotice("");
                        })
                      }
                    >
                      新增{type === "post" ? "圖文" : "Vlog"}
                    </Button>
                  ))}
                </div>
              )}
              {!busy && role && items.length === 0 && <p>還沒有內容草稿。</p>}
              {items.map((item) => (
                <button
                  key={item.contentId}
                  disabled={busy}
                  onClick={() => select(item.contentId)}
                  className="block w-full rounded border p-3 text-left disabled:opacity-50"
                >
                  <strong className="block break-words">
                    {item.displayTitle || item.title || "未命名動態"}
                  </strong>
                  <span className="flex flex-wrap items-center gap-1.5 text-sm">
                    {typeLabels[item.type]} · {labels[item.status]} · v
                    {item.version}
                    {item.type === "story" && item.expiresAt && (
                      <AdminStatusBadge label={storyCountdown(item.expiresAt)} tone="info" />
                    )}
                    {item.audience !== "public" && <AdminStatusBadge label="鐵粉" tone="warning" />}
                  </span>
                </button>
              ))}
              {cursor && (
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => void run((epoch) => load(epoch, true))}
                >
                  載入更多
                </Button>
              )}
            </aside>
            <section className="min-w-0 space-y-4">
              {!draft && <p>選擇一份草稿，或新增內容。</p>}
              {draft && (
                <>
                  {draft.type === "post" && !draft.media?.length && (
                    <p className="text-sm text-muted-foreground">貼文至少需要 1 張照片。可先儲存文字草稿，再到 App 加入照片後送審或發布。</p>
                  )}
                  <p>
                    {typeLabels[draft.type]} · {labels[draft.status]} ·{" "}
                    {draft.version === 0 ? "尚未儲存" : `版本 ${draft.version}`}
                    {draft.type === "story" && draft.expiresAt ? ` · ${storyCountdown(draft.expiresAt)}` : ""}
                    {dirty ? " · 有未儲存變更" : ""}
                  </p>
                  {(draft.status === "published" ||
                    draft.hasPublishedContent) && (
                    <p className="text-sm text-muted-foreground">
                      儲存會建立待發布的修改版本；目前公開內容在重新發布前保持不變。
                    </p>
                  )}
                  {draft.status === "deleted" && (
                    <p className="rounded border p-3 text-sm text-muted-foreground">
                      此內容已刪除
                      {draft.deletedAt
                        ? `（${new Date(draft.deletedAt).toLocaleString("zh-TW")}）`
                        : ""}
                      ，僅供查閱，無法再編輯、送審或發布。
                    </p>
                  )}
                  {draft.reviewNote && (
                    <p className="rounded border p-3">
                      審核意見：{draft.reviewNote}
                    </p>
                  )}
                  {vlogCmsEnabled && draft.type === "vlog" && draft.video && <ArtistVlogPreview key={`${draft.contentId}:${draft.version}:${draft.video.assetId}`}
                    artistId={artistId} contentId={draft.contentId} assetId={draft.video.assetId} version={draft.version} ready={draft.video.status === "ready"} />}
                  {role === "staff" && draft.type === "vlog" && draft.video && <ArtistVlogOperations key={draft.video.assetId} artistId={artistId} contentId={draft.contentId} assetId={draft.video.assetId} />}
                  {role === "staff" && draft.type === "vlog" && draft.coverAssetId && <ArtistVlogCoverCleanup key={draft.coverAssetId} artistId={artistId} contentId={draft.contentId} assetId={draft.coverAssetId} />}
                  {draft.type === "vlog" && <p className="text-sm text-muted-foreground">影片與封面由 App 上傳；此處檢視雲端版本與審核。{draft.coverAssetId ? "已設定封面。" : "尚未設定封面。"}</p>}
                  {!!draft.media?.length && (
                    <p className="text-sm text-muted-foreground">
                      包含 {draft.media.length} 張照片，請在 App
                      預覽照片後發布或送審。
                    </p>
                  )}
                  {draft.type !== "story" && <div>
                    <Label htmlFor="draft-title">
                      {draft.type === "post"
                        ? "標題（選填，最多 80 字元）"
                        : "標題（最多 80 字元）"}
                    </Label>
                    <Input
                      id="draft-title"
                      value={draft.title}
                      maxLength={80}
                      disabled={busy || !editable}
                      onChange={(e) => edit({ title: e.target.value })}
                    />
                  </div>}
                  <div>
                    <Label htmlFor="draft-audience">誰可以看</Label>
                    <select
                      id="draft-audience"
                      className="block w-full rounded border bg-background p-2"
                      value={draft.audience === "member" ? "fans" : draft.audience}
                      disabled={busy || !editable || (draft.hasPublishedContent && draft.audience === "public")}
                      onChange={(e) =>
                        edit({
                          audience: e.target.value as ArtistDraft["audience"],
                        })
                      }
                    >
                      <option value="public">所有人（發布後公開）</option>
                      <option value="fans" disabled={!fansEnabled && draft.audience === "public"}>
                        鐵粉限定（名單內且效期內的帳號才看得到完整內容）{fansEnabled ? "" : "・未開放"}
                      </option>
                    </select>
                    {draft.hasPublishedContent && draft.audience === "public" && (
                      <p className="text-xs text-muted-foreground">已公開過的內容不能改為鐵粉限定（公開圖片可能仍在 CDN 快取）。</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="draft-body">
                      {draft.type === "story"
                        ? "限動文字（選填，一行）"
                        : draft.type === "vlog"
                          ? "影片說明／拍攝筆記"
                          : "圖文正文"}
                      （最多 {draft.type === "story" ? "80" : "5,000"} 字元）
                    </Label>
                    <Textarea
                      id="draft-body"
                      rows={draft.type === "story" ? 2 : 10}
                      maxLength={draft.type === "story" ? 80 : 5000}
                      value={draft.body}
                      disabled={busy || !editable}
                      onChange={(e) => edit({ body: e.target.value })}
                    />
                    <p className="text-sm text-muted-foreground">
                      {draft.body.length} / {draft.type === "story" ? "80" : "5,000"} · 媒體請由 App 上傳
                      {draft.type === "story" && " · 限動發布後 24 小時自動下架，不推播、無公開留言"}
                    </p>
                  </div>
                  {pendingPublication && (
                    <section
                      role="region"
                      aria-label="確認內容變更"
                      className="space-y-3 rounded border p-4"
                    >
                      <p>
                        確定
                        {pendingPublication === "contentPublish"
                          ? "發布"
                          : pendingPublication === "contentDelete"
                            ? "刪除"
                            : "下架"}
                        「{draft.title || draft.body.slice(0, 40) || "照片動態"}
                        」？
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {pendingPublication === "contentPublish"
                          ? "目前僅試點帳號可閱讀。"
                          : pendingPublication === "contentDelete"
                            ? "刪除後無法復原；草稿與公開內容都會標記為已刪除，粉絲端、動態牆與收藏皆不再顯示。"
                            : "粉絲將無法再讀取此內容。"}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant={pendingPublication === "contentDelete" ? "destructive" : "default"}
                          disabled={busy || dirty}
                          onClick={() =>
                            pendingPublication === "contentDelete"
                              ? remove()
                              : publish(pendingPublication)
                          }
                        >
                          確認
                          {pendingPublication === "contentPublish"
                            ? "發布"
                            : pendingPublication === "contentDelete"
                              ? "刪除"
                              : "下架"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setPendingPublication(null)}
                        >
                          取消
                        </Button>
                      </div>
                    </section>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {socialEnabled && (draft.type !== "vlog" || vlogCmsEnabled) &&
                      ["post", "vlog", "story"].includes(draft.type) &&
                      ((["staff", "reviewer"].includes(role || "") &&
                        draft.status === "pending_review") ||
                        (canPublish &&
                          ["draft", "pending_review"].includes(
                            draft.status,
                          ))) && (
                        <Button
                          disabled={
                            busy ||
                            dirty ||
                            draft.version === 0 ||
                            notReady(draft)
                          }
                          onClick={() =>
                            setPendingPublication("contentPublish")
                          }
                        >
                          {draft.type === "vlog" ? "發布 Vlog" : draft.type === "story" ? "發布限動（24 小時）" : "發布文字動態"}
                          {draft.audience !== "public" ? "（鐵粉限定）" : ""}
                        </Button>
                      )}
                    {socialEnabled && (draft.type !== "vlog" || vlogCmsEnabled) &&
                      ["staff", "artist"].includes(role || "") &&
                      draft.audience !== "public" &&
                      (draft.status === "published" ||
                        draft.hasPublishedContent) && (
                        <Button
                          variant="outline"
                          disabled={busy || dirty}
                          onClick={makePublic}
                        >
                          改為所有人可看
                        </Button>
                      )}
                    {socialEnabled &&
                      ["staff", "artist"].includes(role || "") &&
                      (draft.status === "published" ||
                        draft.hasPublishedContent) && (
                        <Button
                          variant="outline"
                          disabled={busy || dirty}
                          onClick={() =>
                            setPendingPublication("contentUnpublish")
                          }
                        >
                          下架內容
                        </Button>
                      )}
                    {socialEnabled &&
                      ["staff", "artist"].includes(role || "") &&
                      draft.version > 0 &&
                      draft.status !== "deleted" && (
                        <Button
                          variant="destructive"
                          disabled={busy || dirty}
                          onClick={() => setPendingPublication("contentDelete")}
                        >
                          刪除內容
                        </Button>
                      )}
                    {editable && (
                      <Button
                        disabled={
                          busy ||
                          (draft.type === "vlog" && !draft.title.trim()) ||
                          (!draft.title.trim() &&
                            !draft.body.trim() &&
                            !draft.media?.length)
                        }
                        onClick={save}
                      >
                        儲存草稿
                      </Button>
                    )}
                    {editable && draft.version > 0 && (
                      <Button
                        variant="outline"
                        disabled={
                          busy ||
                          dirty ||
                          !["post", "vlog", "story"].includes(draft.type) ||
                          notReady(draft) ||
                          draft.status !== "draft"
                        }
                        onClick={() => transition("workspaceSubmit")}
                      >
                        送審
                      </Button>
                    )}
                    {draft.version > 0 && (
                      <Button
                        variant="outline"
                        disabled={busy}
                        onClick={() => select(draft.contentId)}
                      >
                        重新載入此草稿
                      </Button>
                    )}
                  </div>
                  {draft.status === "pending_review" &&
                    (role === "staff" || role === "reviewer") && (
                      <div className="space-y-2">
                        <Label htmlFor="review-note">退回修改原因</Label>
                        <Textarea
                          id="review-note"
                          value={reviewNote}
                          maxLength={1000}
                          disabled={busy}
                          onChange={(e) => setReviewNote(e.target.value)}
                        />
                        <Button
                          variant="outline"
                          disabled={busy || !reviewNote.trim()}
                          onClick={() => transition("workspaceReturn")}
                        >
                          退回修改
                        </Button>
                      </div>
                    )}
                </>
              )}
            </section>
          </div>
        </DialogContent>
      </Dialog>
      {dialog}
    </>
  );
}
